import { useState, useRef, useEffect, useMemo } from "react";
import { MapPin } from "lucide-react";

export default function CityAutocomplete({
  value,
  onChange,
  cities,
  placeholder = "Cerca comune...",
  testId = "city-autocomplete",
}) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const ref = useRef(null);

  const filtered = useMemo(() => {
    if (query.length < 2) return [];
    const q = query.toLowerCase();
    const qNorm = q.replace(/[àá]/g, "a").replace(/[èé]/g, "e").replace(/[ìí]/g, "i").replace(/[òó]/g, "o").replace(/[ùú]/g, "u");
    const starts = [];
    const contains = [];
    for (const c of cities) {
      const n = c.name.toLowerCase();
      const nNorm = n.replace(/[àá]/g, "a").replace(/[èé]/g, "e").replace(/[ìí]/g, "i").replace(/[òó]/g, "o").replace(/[ùú]/g, "u");
      if (n.startsWith(q) || nNorm.startsWith(qNorm)) starts.push(c);
      else if (n.includes(q) || nNorm.includes(qNorm)) contains.push(c);
      if (starts.length + contains.length >= 15) break;
    }
    return [...starts, ...contains].slice(0, 15);
  }, [query, cities]);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (city) => {
    setQuery(city.name);
    onChange(city.name);
    setOpen(false);
    setHighlighted(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlighted >= 0) select(filtered[highlighted]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlighted(-1);
            if (!e.target.value) onChange("");
          }}
          onFocus={() => { if (query.length >= 2) setOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full h-12 pl-10 pr-4 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
          data-testid={testId}
          autoComplete="off"
        />
      </div>
      {open && filtered.length > 0 && (
        <ul
          className="absolute z-50 w-full mt-1 bg-white border-2 border-zinc-200 shadow-lg max-h-60 overflow-y-auto"
          data-testid={`${testId}-dropdown`}
        >
          {filtered.map((city, i) => (
            <li
              key={city.key}
              onClick={() => select(city)}
              className={`px-4 py-2.5 cursor-pointer text-sm font-body flex items-center gap-2 transition-colors ${
                i === highlighted
                  ? "bg-[#FF5200]/10 text-[#FF5200]"
                  : "text-zinc-700 hover:bg-zinc-50"
              }`}
            >
              <MapPin size={14} className="text-zinc-400 flex-shrink-0" />
              <span className="flex-1">{city.name}</span>
              {city.prov && (
                <span className="text-xs text-zinc-400 font-heading tracking-wide">{city.prov}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && query.length >= 2 && filtered.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border-2 border-zinc-200 shadow-lg px-4 py-3 text-sm text-zinc-400 font-body">
          Nessun comune trovato
        </div>
      )}
    </div>
  );
}
